'use client'

import { User } from 'lucide-react'
import Image, { type ImageProps } from 'next/image'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { useTenantMediaDefaults } from '@/hooks/use-tenant-media-defaults'

import { AvatarImage } from '@/components/ui/avatar'

export function useResolvedAvatarSrc(avatar?: string | null) {
  const { resolveAvatarUrl } = useTenantMediaDefaults()
  return resolveAvatarUrl(avatar)
}

export function useResolvedThumbnailSrc(thumbnail?: string | null) {
  const { resolveThumbnailUrl } = useTenantMediaDefaults()
  return resolveThumbnailUrl(thumbnail)
}

interface ResolvedAvatarImageProps extends React.ComponentPropsWithoutRef<typeof AvatarImage> {
  avatar?: string | null
}

export function ResolvedAvatarImage({ avatar, alt, ...props }: ResolvedAvatarImageProps) {
  const src = useResolvedAvatarSrc(avatar)
  if (!src) {
    return null
  }

  return <AvatarImage src={src} alt={alt} {...props} />
}

interface ResolvedUserImageProps extends Omit<ImageProps, 'src' | 'alt'> {
  avatar?: string | null
  alt: string
  size?: number
  placeholderClassName?: string
  onClick?: React.MouseEventHandler<HTMLElement>
}

export function ResolvedUserImage({
  avatar,
  alt,
  size = 40,
  className,
  placeholderClassName,
  onClick,
  ...props
}: ResolvedUserImageProps) {
  const src = useResolvedAvatarSrc(avatar)

  if (!src) {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground',
          onClick && 'cursor-pointer',
          placeholderClassName,
          className
        )}
        style={{ width: size, height: size }}
        aria-label={alt}
        role={onClick ? 'button' : 'img'}
        onClick={onClick}
        onKeyDown={
          onClick
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onClick(event as unknown as React.MouseEvent<HTMLElement>)
                }
              }
            : undefined
        }
        tabIndex={onClick ? 0 : undefined}
      >
        <User className="size-[55%]" aria-hidden="true" />
      </div>
    )
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={size}
      height={size}
      className={cn('rounded-full object-cover', onClick && 'cursor-pointer', className)}
      onClick={onClick}
      {...props}
    />
  )
}

interface ResolvedContentThumbnailProps extends Omit<ImageProps, 'src' | 'alt'> {
  thumbnail?: string | null
  alt: string
  placeholderClassName?: string
}

export function ResolvedContentThumbnail({
  thumbnail,
  alt,
  className,
  placeholderClassName,
  width = 400,
  height = 240,
  ...props
}: ResolvedContentThumbnailProps) {
  const src = useResolvedThumbnailSrc(thumbnail)

  if (!src) {
    return (
      <div
        className={cn('flex w-full items-center justify-center bg-muted text-muted-foreground', placeholderClassName, className)}
        style={{ minHeight: typeof height === 'number' ? height : undefined }}
        aria-label={alt}
        role="img"
      >
        <User className="size-10 opacity-40" aria-hidden="true" />
      </div>
    )
  }

  return <Image src={src} alt={alt} width={width} height={height} className={className} {...props} />
}
